/**
 * Consent Manager - Shared consent management for marketing pixels and tracking
 */

import type { ConsentDependencies } from '../types/dependencies.types';
import { DependencyHelper } from '../core/dependency-helper';

type ConsentCallback = () => void;

export class ConsentManager {
  private readonly CONSENT_KEY = 'vocalist_cookie_consent';
  private consentGiven: boolean = false;
  private initialized: boolean = false;
  private callbacks: ConsentCallback[] = [];
  // Essential analytics tier (Statsig + Amplitude + the Google tag via Consent Mode v2): granted on
  // ANY affirmative banner choice ("Accept All" OR "Essential Only") or when consent isn't required —
  // a superset of `consentGiven`. The other marketing pixels (Meta/Apphud/ProfitWell) keep gating on
  // `consentGiven` (full consent only); the Google tag loads on essential and signals granted/denied.
  private essentialConsentGiven: boolean = false;
  private essentialCallbacks: ConsentCallback[] = [];
  private deps: ConsentDependencies = {} as ConsentDependencies;

  // List of countries requiring strict prior consent (GDPR compliance)
  private readonly STRICT_CONSENT_COUNTRIES = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE', // EU
    'IS',
    'LI',
    'NO', // EEA
    'GB', // UK
    'CH', // Switzerland
  ];

  /**
   * Initialize Consent Manager with dependencies
   */
  init(deps: Partial<ConsentDependencies> = {}): void {
    // Validate and fix dependencies
    this.deps = DependencyHelper.validateAndFixDependencies(
      deps,
      ['statsManager'], // required dependencies
      'ConsentManager'
    );

    if (this.initialized) {
      return;
    }

    // Check for existing consent
    const consent = this.getStoredConsent();

    // Determine if consent is required based on user's location
    const requiresConsent = this.requiresStrictConsent();

    if (!requiresConsent) {
      // User is not in a strict consent region, enable tracking by default
      console.log('Consent not required for country:', (window as any).USER_COUNTRY || 'unknown');
      this.consentGiven = true;
      this.grantEssential();
      this.notifyCallbacks();
      this.deps.statsManager.track('consent', {
        type: 'skip',
        country: (window as any).USER_COUNTRY || 'unknown',
      }, 'skip');
    } else if (consent === null) {
      // No consent stored and user is in strict consent region, show banner.
      // Neither tier is granted until the user makes a choice.
      console.log('Showing consent banner for country:', (window as any).USER_COUNTRY || 'unknown');
      this.showConsentBanner();
    } else if (consent === true) {
      // Full consent previously given ("Accept All").
      this.consentGiven = true;
      this.grantEssential();
      this.notifyCallbacks();
      this.deps.statsManager.track('consent', {
        type: 'stored_yes',
        country: (window as any).USER_COUNTRY || 'unknown',
      }, 'stored_yes');
    } else if (consent === false) {
      // "Essential Only" previously chosen: essential analytics on, marketing pixels off.
      this.grantEssential();
    }

    // Set up event listeners for consent buttons
    this.setupConsentListeners();
    this.initialized = true;
    console.log('ConsentManager initialized');
  }

  /**
   * Check if user's location requires strict consent
   */
  private requiresStrictConsent(): boolean {
    const country = (window as any).USER_COUNTRY as string | undefined;

    // If country is unknown, fail-safe: require consent
    if (!country) {
      console.log('Country unknown, requiring consent as fail-safe');
      return true;
    }

    // Check if country is in the strict consent list
    return this.STRICT_CONSENT_COUNTRIES.includes(country);
  }

  /**
   * Register a callback to be notified when consent is given
   */
  onConsentGranted(callback: ConsentCallback): void {
    if (typeof callback === 'function') {
      if (this.consentGiven) {
        // If consent is already given, call the callback immediately
        callback();
      } else {
        // Otherwise, register the callback for later notification
        this.callbacks.push(callback);
      }
    } else {
      console.error('Callback is not a function');
    }
  }

  /**
   * Notify all registered callbacks that consent has been granted
   */
  private notifyCallbacks(): void {
    this.callbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error in consent callback:', error);
      }
    });
  }

  /**
   * Register a callback for when ESSENTIAL consent is granted (Statsig + Amplitude).
   * Fires on "Accept All" OR "Essential Only", or immediately where consent isn't required.
   */
  onEssentialConsentGranted(callback: ConsentCallback): void {
    if (typeof callback !== 'function') {
      console.error('Callback is not a function');
      return;
    }
    if (this.essentialConsentGiven) {
      callback();
    } else {
      this.essentialCallbacks.push(callback);
    }
  }

  /**
   * Grant essential consent once (idempotent) and notify essential callbacks.
   */
  private grantEssential(): void {
    if (this.essentialConsentGiven) {
      return;
    }
    this.essentialConsentGiven = true;
    this.essentialCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error in essential consent callback:', error);
      }
    });
  }

  /**
   * Whether essential analytics consent has been granted.
   */
  hasEssentialConsent(): boolean {
    return this.essentialConsentGiven;
  }

  /**
   * Get stored consent from localStorage
   */
  private getStoredConsent(): boolean | null {
    const stored = localStorage.getItem(this.CONSENT_KEY);
    if (stored === null) return null;
    return stored === 'true';
  }

  /**
   * Store consent decision
   */
  private storeConsent(granted: boolean): void {
    localStorage.setItem(this.CONSENT_KEY, granted.toString());
  }

  /**
   * Check if consent has been given
   */
  hasConsent(): boolean {
    return this.consentGiven;
  }

  /**
   * Show cookie consent banner
   */
  private showConsentBanner(): void {
    const banner = document.getElementById('cookie-consent-banner');
    if (banner) {
      banner.classList.add('show');
    }
  }

  /**
   * Hide cookie consent banner
   */
  private hideConsentBanner(): void {
    const banner = document.getElementById('cookie-consent-banner');
    if (banner) {
      banner.classList.remove('show');
    }
  }

  /**
   * Show cookie settings popup
   */
  private showSettingsPopup(): void {
    const popup = document.getElementById('cookie-settings-popup');
    if (popup) {
      popup.classList.add('show');
    }
  }

  /**
   * Hide cookie settings popup
   */
  private hideSettingsPopup(): void {
    const popup = document.getElementById('cookie-settings-popup');
    if (popup) {
      popup.classList.remove('show');
    }
  }

  /**
   * Setup consent button listeners
   */
  private setupConsentListeners(): void {
    // Banner "Accept All" button
    const acceptBannerBtn = document.getElementById('cookie-accept-all-banner');
    if (acceptBannerBtn) {
      acceptBannerBtn.addEventListener('click', () => {
        this.handleConsentAccept();
      });
    }

    // Banner "Cookie Settings" link
    const settingsLink = document.getElementById('cookie-settings-link');
    if (settingsLink) {
      settingsLink.addEventListener('click', () => {
        this.hideConsentBanner();
        this.showSettingsPopup();
      });
    }

    // Popup "Accept All" button
    const acceptPopupBtn = document.getElementById('cookie-accept-all-popup');
    if (acceptPopupBtn) {
      acceptPopupBtn.addEventListener('click', () => {
        this.handleConsentAccept();
        this.hideSettingsPopup();
      });
    }

    // Popup "Essential Only" button
    const essentialBtn = document.getElementById('cookie-accept-essential');
    if (essentialBtn) {
      essentialBtn.addEventListener('click', () => {
        this.handleConsentDecline();
        this.hideSettingsPopup();
      });
    }

    // Close popup when clicking outside
    const popup = document.getElementById('cookie-settings-popup');
    if (popup) {
      popup.addEventListener('click', (e) => {
        if (e.target === popup) {
          this.hideSettingsPopup();
          this.showConsentBanner();
        }
      });
    }
  }

  /**
   * Handle consent acceptance
   */
  private handleConsentAccept(): void {
    console.log('Cookie consent: All cookies accepted');
    this.consentGiven = true;
    this.storeConsent(true);
    this.hideConsentBanner();
    this.hideSettingsPopup();

    // Notify essential (Statsig/Amplitude) + full (marketing pixels) callbacks.
    this.grantEssential();
    this.notifyCallbacks();

    // Track consent event using StatsManager
    if (this.deps.statsManager) {
      this.deps.statsManager.track('consent', {
        type: 'yes',
        country: (window as any).USER_COUNTRY || 'unknown',
      }, 'yes');
    }
  }

  /**
   * Handle consent decline (essential only)
   */
  private handleConsentDecline(): void {
    console.log('Cookie consent: Essential only');
    this.consentGiven = false;
    this.storeConsent(false);
    this.hideConsentBanner();
    this.hideSettingsPopup();

    // Essential analytics (Statsig/Amplitude) are allowed under "Essential Only"; marketing pixels are not.
    this.grantEssential();

    // Track consent event using StatsManager
    if (this.deps.statsManager) {
      this.deps.statsManager.track('consent', {
        type: 'no',
        country: (window as any).USER_COUNTRY || 'unknown',
      }, 'no');
    }
  }

  /**
   * Revoke consent (for testing or user preference changes)
   */
  revokeConsent(): void {
    this.consentGiven = false;
    this.storeConsent(false);
    console.log('Marketing consent revoked');

    // Note: Individual pixel managers should handle their own cleanup
    // when checking consent status
  }
}

// Create singleton instance
const consentManager = new ConsentManager();

// Make globally available for backward compatibility
if (typeof window !== 'undefined') {
  (window as any).ConsentManager = consentManager;
}

export default consentManager;
